import { useAppTheme } from "../../../hooks/useAppTheme";
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  Modal as RNModal,
  View as RNView,
  Pressable,
  Platform,
} from "react-native";
import { DataTable } from "react-native-paper";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { default as Modal } from "../../../components/custom/Modal";
import Spinner from "../../../components/shared/Spinner";
import useColorScheme from "../../../hooks/useColorScheme";
import { getAllProducts } from "../../../services/productService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headersProduct,
  mediumDeviceHeadersProduct as mdProduct,
  smallDeviceHeadersProduct as smProduct,
} from "../utils/productHeaders";
import { productModel } from "../../../models/reportsModel";
import { validSignedURLWithExt } from "../../../utils/validateSignedUrl";
import { handleTableLoader } from "../../../utils/common";
import { useSelector } from "react-redux";
import { userClaimInformation } from "../../../models/usersModel";
import { useIsFocused } from "@react-navigation/native";
import { sortColumns } from "../../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { ReportsDispatcher } from "../../../redux/dispatcher/reportsDispatcher";
import { actionTypesReports } from "../../../redux/constants/actionTypesReports";
import { formatDate } from "../../../utils/dateUtility";
import {
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { productReportTranslationMap } from "../../../utils/translations";
import AsyncStorage from "@react-native-async-storage/async-storage";
// @ts-ignore
import { Feather, Ionicons } from "@expo/vector-icons";

export default function ProductsReportScreen({ route, navigation }: any) {
  const { colors, isDark } = useAppTheme();
  const productFilterStyles = React.useMemo(() => createProductFilterStyles(colors), [colors]);
  const colorScheme = useColorScheme();
  const isFocused = useIsFocused();
  const styles = React.useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const cardStyles = React.useMemo(() => createCardStyles(colors, isDark), [colors, isDark]);
  const { t } = useTranslation();
  const listRef = useRef<ScrollView>(null);

  const [productsPageLevelList, setProductsPageLevelList] = useState<
    Array<productModel>
  >([]);

  const [isSearched, setIsSearched] = useState<boolean>(false);

  const { userInfo, language } = useSelector(
    (state: any) => state.configurations,
  );
  const {
    productReportList,
    isProductReportListLoaded,
    productReportFilterCategoryList,
    lastRefreshProductReport,
  } = useSelector((state: any) => state.reports);

  const [selected, setSelected] = useState<productModel>({} as productModel);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headersProduct,
    );
  const [searchedText, setSearchedText] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [lastRefresh, setLastRefresh] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [, setFabOpen] = useState<Record<string, boolean>>({
    open: false,
  });
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("products_report_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
    AsyncStorage.getItem("products_report_column_order").then((stored) => {
      if (stored) {
        try {
          setColumnOrder(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  useEffect(() => {
    navigation.setOptions({ title: t("product") });
  }, [navigation, language, t]);

  // All available headers based on device size
  const getAllHeaders = () => {
    const base = isLargeDevice
      ? headersProduct
      : isMobileDevice
        ? smProduct
        : mdProduct;
    return translateHeaders(base, t, productReportTranslationMap);
  };

  const getOrderedKeys = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>,
  ) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => {
      if (allKeys.includes(k)) ordered.push(k);
    });
    allKeys.forEach((k) => {
      if (!ordered.includes(k)) ordered.push(k);
    });
    return ordered;
  };

  const getFilteredHeaders = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>,
  ) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map(
        (key) =>
          [key, baseHeaders[key]] as [
            string,
            Record<string, string | number | boolean>,
          ],
      );
    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, h) => sum + (Number(h.width) || 0),
      0,
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, h]) => sum + (Number(h.width) || 0),
      0,
    );
    const scale =
      totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<
      string,
      Record<string, string | number | boolean>
    > = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change or device size changes
  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMobileDevice, isMediumDevice, language]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "products_report_hidden_columns",
        JSON.stringify(updated),
      );
      return updated;
    });
  };

  const moveColumn = useCallback(
    (fromKey: string, toKey: string) => {
      const allHeaders = getAllHeaders();
      const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
      const fromIdx = currentKeys.indexOf(fromKey);
      const toIdx = currentKeys.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const newKeys = [...currentKeys];
      newKeys.splice(fromIdx, 1);
      newKeys.splice(toIdx, 0, fromKey);
      newKeys.push("");
      setColumnOrder(newKeys);
      AsyncStorage.setItem(
        "products_report_column_order",
        JSON.stringify(newKeys),
      );
    },
    [columnOrder],
  );

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("products_report_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({
    activeKey: null,
    overKey: null,
  });

  const startColumnDrag = useCallback(
    (key: string) => {
      if (Platform.OS !== "web" || typeof window === "undefined") return;
      dragRef.current.activeKey = key;
      setDragActiveKey(key);

      const onMouseMove = (e: MouseEvent) => {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const row =
          el && (el as HTMLElement).closest
            ? (el as HTMLElement).closest("[data-columnkey]")
            : null;
        const overKey = row ? row.getAttribute("data-columnkey") : null;
        if (overKey !== dragRef.current.overKey) {
          dragRef.current.overKey = overKey;
          setDragOverKey(
            overKey && overKey !== dragRef.current.activeKey ? overKey : null,
          );
        }
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        (document.body.style as any).userSelect = "";
        const from = dragRef.current.activeKey;
        const to = dragRef.current.overKey;
        if (from && to && from !== to) {
          moveColumn(from, to);
        }
        dragRef.current = { activeKey: null, overKey: null };
        setDragActiveKey(null);
        setDragOverKey(null);
      };

      document.body.style.cursor = "grabbing";
      (document.body.style as any).userSelect = "none";
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [moveColumn],
  );

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [_filterCategory, _setFilterCategory] = useState<string>("all");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);

  const [mobilePage, setMobilePage] = useState<number>(0);
  const itemsPerPage = 20;

  useEffect(() => {
    setMobilePage(0);
  }, [productsPageLevelList]);

  const MobileProductCard = ({ product }: { product: productModel }) => {
    return (
      <TouchableOpacity
        style={cardStyles.card}
        onPress={() => {
          setSelected(product);
          setModalVisible(true);
        }}
        activeOpacity={0.7}
      >
        <RNView style={cardStyles.cardHeader}>
          <RNView style={cardStyles.cardHeaderLeft}>
            <RNView style={cardStyles.containerIconWrap}>
              <Ionicons name="cube-outline" size={18} color={isDark ? colors.primary : "#3B82F6"} />
            </RNView>
            <RNView style={{ flex: 1, backgroundColor: "transparent" }}>
              <Text style={cardStyles.containerNumber}>{product.ItemName}</Text>
            </RNView>
          </RNView>
        </RNView>

        <View style={cardStyles.divider} />
        <View style={cardStyles.dateRow}>
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>{t("pcsCarton") ? t("pcsCarton").toUpperCase() : "PCS (CARTON)"}</Text>
            <Text style={cardStyles.dateValue}>{Number(product.PerCtns).toLocaleString("en-US")}</Text>
          </View>
          <View style={{ width: 1, height: 24, backgroundColor: isDark ? colors.border : "#E2E8F0" }} />
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>{t("ctnsPerPallet") ? t("ctnsPerPallet").toUpperCase() : "CTNS (PALLET)"}</Text>
            <Text style={cardStyles.dateValue}>{Number(product.PerPallet).toLocaleString("en-US")}</Text>
          </View>
        </View>

        <View style={cardStyles.divider} />
        <View style={cardStyles.cardFooter}>
          <View style={cardStyles.metaChip}>
            <Ionicons name="pricetag-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
            <Text style={cardStyles.metaChipText}>{product.ItemCode}</Text>
          </View>
          <View style={cardStyles.metaChip}>
            <Ionicons name="business-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
            <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{product.CardName}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const handleScroll = (event: any) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
    setContentOffSet(contentOffset.y);

    if (layoutMeasurement.height + contentOffset.y >= contentSize.height - 100) {
      setIsAtBottom(true);
    } else {
      setIsAtBottom(false);
    }
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedProductsReportList: Array<productModel> = sortColumns(
      productsPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders,
    );

    setProductsPageLevelList(_sortedProductsReportList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      setSearchedText(searchText);

      const filteredListBasedOnSearchOrCondition: Array<productModel> =
        filterBasedOnAnyKey(productReportList, searchText);

      const _filterProductByCategory = filterProductByCategory(
        filteredListBasedOnSearchOrCondition,
        _filterCategory,
        selectedCategories,
      );

      //filter based on tags or display switch
      setProductsPageLevelList(
        _isDisplayViewOn ? _filterProductByCategory : _filterProductByCategory,
      );
    } else {
      setSearchedText("");
      const _filterProductByCategory = filterProductByCategory(
        productReportList,
        _filterCategory,
        selectedCategories,
      );
      setProductsPageLevelList(_filterProductByCategory);
    }

    fadeTableLoader();
  };

  const onFabOpenChange = ({ open }: { open: boolean }) => setFabOpen({ open });

  const filterProductByCategory = (
    _productsReport: Array<productModel>,
    _filterCategory: string,
    categoriesArr?: string[],
  ) => {
    const activeCategories =
      categoriesArr && categoriesArr.length > 0
        ? categoriesArr
        : _filterCategory !== "all"
          ? [_filterCategory]
          : [];
    if (activeCategories.length === 0) return _productsReport;

    const matchesCategory = (product: productModel, cat: string): boolean => {
      switch (cat) {
        case "produce":
          return [101, 102, 103, 104, 106].includes(product.ItmsGrpCod);
        case "berry":
          return product.ItmsGrpCod === 100;
        case "meat":
          return product.ItmsGrpCod === 110;
        case "meals":
          return [111, 116].includes(product.ItmsGrpCod);
        case "salad":
          return product.ItmsGrpCod === 107;
        case "bakery":
          return product.ItmsGrpCod === 108;
        default:
          return true;
      }
    };

    return _productsReport.filter((product: productModel) =>
      activeCategories.some((cat) => matchesCategory(product, cat)),
    );
  };

  useEffect(() => {
    setLastRefresh(lastRefreshProductReport);
  }, [lastRefreshProductReport]);

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;

    if (isMounted) {
      //To restrict fetching data from endpoint, if data is available in store
      if (productsPageLevelList.length === 0 || isRefresh) {
        displayTableLoader();

        //set filtering by category & display by carton to default
        _setIsDisplayViewOn(false);
        _setFilterCategory("all");
        setSearchedText("");

        //set last refresh date
        const formattedLastRefreshDate = formatDate(new Date(), "short", true);
        ReportsDispatcher(
          actionTypesReports.LastRefreshProductReport,
          formattedLastRefreshDate,
        );

        getAllProducts(_userInfo.team, _userInfo.zone_info, tableHeaders);

        fadeTableLoader();
      }
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh]);

  useEffect(() => {
    setImageLoading(true);
  }, [modalVisible]);

  useEffect(() => {
    displayTableLoader();

    const _productsReport = _isDisplayViewOn
      ? productsPageLevelList
      : (productReportList as Array<productModel>);
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_productsReport, searchedText)
        : _productsReport;

    if (_isDisplayViewOn) {
      setProductsPageLevelList(filteredListBasedOnSearchOrCondition);
    } else {
      setProductsPageLevelList(
        filterProductByCategory(
          filteredListBasedOnSearchOrCondition,
          _filterCategory,
          selectedCategories,
        ),
      );
    }

    fadeTableLoader();
  }, [_isDisplayViewOn]);

  useEffect(() => {
    displayTableLoader();

    const _productsReport = productReportList as Array<productModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_productsReport, searchedText)
        : _productsReport;

    if (_isDisplayViewOn) {
      setProductsPageLevelList(
        filterProductByCategory(
          filteredListBasedOnSearchOrCondition,
          _filterCategory,
          selectedCategories,
        ),
      );
    } else {
      setProductsPageLevelList(
        filterProductByCategory(
          filteredListBasedOnSearchOrCondition,
          _filterCategory,
          selectedCategories,
        ),
      );
    }

    fadeTableLoader();
  }, [_filterCategory, selectedCategories]);

  useEffect(() => {
    displayTableLoader();

    const _productsReport = productReportList as Array<productModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_productsReport, searchedText)
        : _productsReport;

    if (_filterCategory === "all" && !_isDisplayViewOn) {
      setProductsPageLevelList(filteredListBasedOnSearchOrCondition);
    }

    fadeTableLoader();
  }, [
    productReportList,
    _isDisplayViewOn,
    _filterCategory === "all" && selectedCategories.length === 0,
  ]);

  useEffect(() => {
    validSignedURLWithExt(
      selected.PicturName || "",
      "label",
      setImageLoading,
      setSelectedImage,
    );
  }, [selected]);

  const ModalContent = (props: any) => {
    const _excludedKeys = [
      "ItemCode",
      "PerCtns",
      "PerPallet",
      "FrgnName",
      "CardName",
    ];

    const _orderedKeys = [
      "ItemCode",
      "PerCtns",
      "PerPallet",
      "FrgnName",
      "CardName",
    ];

    const _specialLabels = {
      PerPallet: {
        oldLabel: "PerPallet",
        newLabel: t("totalPerPallet"),
        newValue: (selected.PerPallet * selected.PerCtns) as unknown as string,
        supportingValue: ((selected.PerPallet * selected.PerCtns) /
          selected.PerCtns) as unknown as string,
      },
      ItmsGrpCod: {
        oldLabel: "ItmsGrpCod",
        newLabel: "Display",
        newValue: _isDisplayViewOn ? t("perCartonLabel") : t("perPieceLabel"),
      },
    };

    return (
      <Modal.ModalContent
        {...props}
        title={selected.ItemCode || ""}
        subTitle={selected.FrgnName}
        icon="folder"
        uri={selectedImage}
        loading={isImageLoading}
        childern={
          <Modal.ModalBody
            listData={selected}
            excludedKeys={_excludedKeys}
            renameList={headersProduct}
            orderedKeys={_orderedKeys}
            specialLabels={_specialLabels}
          />
        }
      />
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }}>
      <ScrollView style={{ flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }} contentContainerStyle={{ flexGrow: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }} onScroll={handleScroll} ref={listRef}>
        <View
          style={[
            styles.pageWrapper,
            { backgroundColor: columnSettingsOpen ? colors.primaryBg : (isDark ? colors.background : "#eef2f6") }
          ]}
        >
          {isProductReportListLoaded ? (
            <>
              <View style={[styles.tableCard, !isSmallDevice && { marginTop: 12 }, isSmallDevice && { marginBottom: 8 }]}>
                <LabelBannerWithSearchBar
                  titleName={`${t("totalItems")}: ${productsPageLevelList.length}`}
                  handleSearch={handleSearch}
                  setRefresh={setRefresh}
                  dataToExport={[]}
                  fileName="productReportScreen"
                  sheetName="Product Report Screen"
                  middleSearchControls={
                    (isSmallDevice || isMobileDevice) ? (
                      <TouchableOpacity
                        style={[
                          {
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            backgroundColor: showMobileFilters ? colors.primary : colors.surfaceSubtle,
                            justifyContent: "center",
                            alignItems: "center",
                            borderWidth: 1,
                            borderColor: showMobileFilters ? colors.primary : colors.border,
                          },
                          (isSmallDevice || isMobileDevice) && {
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                          }
                        ]}
                        onPress={() => setShowMobileFilters(!showMobileFilters)}
                      >
                        <Feather
                          name="filter"
                          size={(isSmallDevice || isMobileDevice) ? 14 : 15}
                          color={showMobileFilters ? "#FFFFFF" : "#6B7280"}
                        />
                      </TouchableOpacity>
                    ) : undefined
                  }
                  preSearchControls={
                    !(isSmallDevice || isMobileDevice) ? (
                      <RNView>
                        <TouchableOpacity
                          ref={settingsBtnRef}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            backgroundColor: colors.surfaceSubtle,
                            justifyContent: "center",
                            alignItems: "center",
                            borderWidth: 1,
                            borderColor: columnSettingsOpen ? colors.primary : colors.border,
                          }}
                          onPress={() => {
                            if (settingsBtnRef.current) {
                              settingsBtnRef.current.measureInWindow(
                                (
                                  x: number,
                                  y: number,
                                  width: number,
                                  height: number,
                                ) => {
                                  setSettingsBtnPos({
                                    x: x || 0,
                                    y: y || 0,
                                    width: width || 0,
                                    height: height || 0,
                                  });
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                },
                              );
                            } else {
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          }}
                        >
                          <Feather
                            name="settings"
                            size={15}
                            color={columnSettingsOpen ? colors.primary : colors.textSecondary}
                          />
                        </TouchableOpacity>
                        {columnSettingsOpen && (
                          <RNModal
                            transparent={true}
                            visible={columnSettingsOpen}
                            animationType="fade"
                            onRequestClose={() => setColumnSettingsOpen(false)}
                          >
                            <Pressable
                              style={{
                                flex: 1,
                                backgroundColor: "rgba(0,0,0,0.15)",
                              }}
                              onPress={() => setColumnSettingsOpen(false)}
                            >
                              <Pressable
                                style={{
                                  position: "absolute",
                                  top: settingsBtnPos.y + settingsBtnPos.height + 6,
                                  left: settingsBtnPos.x - 180 + 40,
                                  backgroundColor: colors.surface,
                                  borderRadius: 12,
                                  borderWidth: 1,
                                  borderColor: colors.border,
                                  paddingVertical: 10,
                                  minWidth: 220,
                                  shadowColor: "#000",
                                  shadowOffset: { width: 0, height: 8 },
                                  shadowOpacity: 0.15,
                                  shadowRadius: 20,
                                  elevation: 10,
                                }}
                                onPress={(e) => e.stopPropagation()}
                              >
                                <Text
                                  style={{
                                    fontSize: 12,
                                    fontWeight: "700",
                                    color: colors.textSecondary,
                                    paddingHorizontal: 16,
                                    paddingBottom: 8,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  {t("showHideColumns")}
                                </Text>
                                <View
                                  style={{
                                    height: 1,
                                    backgroundColor: colors.borderSubtle,
                                    marginBottom: 4,
                                  }}
                                />
                                {/* Select All */}
                                <TouchableOpacity
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    paddingVertical: 8,
                                    paddingHorizontal: 16,
                                  }}
                                  onPress={() => {
                                    const allKeys = Object.keys(getAllHeaders()).filter(
                                      (k) => k !== "",
                                    );
                                    if (hiddenColumns.length === 0) {
                                      const updated = [...allKeys];
                                      setHiddenColumns(updated);
                                      AsyncStorage.setItem(
                                        "products_report_hidden_columns",
                                        JSON.stringify(updated),
                                      );
                                    } else {
                                      setHiddenColumns([]);
                                      AsyncStorage.setItem(
                                        "products_report_hidden_columns",
                                        JSON.stringify([]),
                                      );
                                    }
                                  }}
                                >
                                  <View
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 4,
                                      borderWidth: 1.5,
                                      borderColor:
                                        hiddenColumns.length === 0
                                          ? colors.primary
                                          : colors.textMuted,
                                      backgroundColor:
                                        hiddenColumns.length === 0 ? colors.primary : colors.surface,
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 10,
                                    }}
                                  >
                                    {hiddenColumns.length === 0 && (
                                      <Feather name="check" size={13} color="#fff" />
                                    )}
                                  </View>
                                  <Text
                                    style={{
                                      fontSize: 14,
                                      color: colors.text,
                                      fontWeight: "700",
                                    }}
                                  >
                                    {t("selectAll")}
                                  </Text>
                                </TouchableOpacity>
                                <View
                                  style={{
                                    height: 1,
                                    backgroundColor: colors.borderSubtle,
                                    marginBottom: 2,
                                  }}
                                />
                                <ScrollView
                                  style={{ maxHeight: 300 }}
                                  showsVerticalScrollIndicator={true}
                                >
                                  {getOrderedKeys(getAllHeaders()).map((key) => {
                                    if (key === "") return null;
                                    const value = getAllHeaders()[key];
                                    if (!value) return null;
                                    const isVisible = !hiddenColumns.includes(key);
                                    const isDragging = dragActiveKey === key;
                                    const isDragOver = dragOverKey === key;
                                    return (
                                      <View
                                        key={key}
                                        {...({ dataSet: { columnkey: key } } as any)}
                                        style={{
                                          flexDirection: "row",
                                          alignItems: "center",
                                          paddingVertical: 8,
                                          paddingHorizontal: 16,
                                          opacity: isDragging ? 0.35 : 1,
                                          backgroundColor: isDragOver
                                            ? colors.primaryBg
                                            : "transparent",
                                          borderTopWidth: isDragOver ? 2 : 0,
                                          borderTopColor: colors.primary,
                                        }}
                                      >
                                        <Pressable
                                          onPressIn={() => startColumnDrag(key)}
                                          style={
                                            {
                                              padding: 4,
                                              marginRight: 6,
                                              cursor: "grab",
                                            } as any
                                          }
                                        >
                                          <Feather
                                            name="menu"
                                            size={14}
                                            color={colors.textMuted}
                                          />
                                        </Pressable>
                                        <TouchableOpacity
                                          style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            flex: 1,
                                          }}
                                          onPress={() => toggleColumn(key)}
                                        >
                                          <View
                                            style={{
                                              width: 20,
                                              height: 20,
                                              borderRadius: 4,
                                              borderWidth: 1.5,
                                              borderColor: isVisible
                                                ? colors.primary
                                                : colors.textMuted,
                                              backgroundColor: isVisible
                                                ? colors.primary : colors.surface,
                                              justifyContent: "center",
                                              alignItems: "center",
                                              marginRight: 10,
                                            }}
                                          >
                                            {isVisible && (
                                              <Feather
                                                name="check"
                                                size={13}
                                                color="#fff"
                                              />
                                            )}
                                          </View>
                                          <Text
                                            style={{
                                              fontSize: 14,
                                              color: isVisible ? colors.text : colors.textMuted,
                                              fontWeight: isVisible ? "600" : "400",
                                            }}
                                          >
                                            {(value as any).name}
                                          </Text>
                                        </TouchableOpacity>
                                      </View>
                                    );
                                  })}
                                </ScrollView>
                                {columnOrder.length > 0 && (
                                  <>
                                    <View
                                      style={{
                                        height: 1,
                                        backgroundColor: colors.borderSubtle,
                                        marginTop: 4,
                                      }}
                                    />
                                    <TouchableOpacity
                                      style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        paddingVertical: 10,
                                        paddingHorizontal: 16,
                                      }}
                                      onPress={resetColumnOrder}
                                    >
                                      <Feather
                                        name="rotate-ccw"
                                        size={13}
                                        color={colors.textSecondary}
                                      />
                                      <Text
                                        style={{
                                          fontSize: 13,
                                          color: colors.textSecondary,
                                          fontWeight: "600",
                                          marginLeft: 6,
                                        }}
                                      >
                                        {t("resetOrder")}
                                      </Text>
                                    </TouchableOpacity>
                                  </>
                                )}
                              </Pressable>
                            </Pressable>
                          </RNModal>
                        )}

                      </RNView>
                    ) : undefined
                  }
                />
                {((isSmallDevice || isMobileDevice) ? showMobileFilters : true) && (
                  <View style={productFilterStyles.renderFilterStyles}>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={productFilterStyles.filterContainer}
                    >
                      {!(isSmallDevice || isMobileDevice) && (
                        <Text style={productFilterStyles.quickFilterText}>
                          {t("quickFilter")}
                        </Text>
                      )}
                      {productReportFilterCategoryList.map(
                        (category: string, index: number) => {
                        const isActive =
                          category === "all"
                            ? selectedCategories.length === 0
                            : selectedCategories.includes(category);
                        return (
                          <TouchableOpacity
                            key={index}
                            style={[
                              productFilterStyles.filterButton,
                              isActive &&
                              productFilterStyles.activeFilterButton,
                            ]}
                            onPress={() => {
                              if (category === "all") {
                                setSelectedCategories([]);
                                _setFilterCategory("all");
                              } else {
                                const newCats = selectedCategories.includes(
                                  category,
                                )
                                  ? selectedCategories.filter(
                                    (c) => c !== category,
                                  )
                                  : [...selectedCategories, category];
                                setSelectedCategories(newCats);
                                _setFilterCategory(
                                  newCats.length === 0
                                    ? "all"
                                    : newCats.join(","),
                                );
                              }
                            }}
                          >
                            <Text
                              style={
                                isActive
                                  ? productFilterStyles.activeFilterText
                                  : productFilterStyles.filterText
                              }
                            >
                              {category === "all"
                                ? t("all")
                                : category.charAt(0).toUpperCase() +
                                category.slice(1)}
                            </Text>
                          </TouchableOpacity>
                        );
                      },
                    )}
                  </ScrollView>
                </View>
                )}
                {!isSmallDevice && (
                  <DataTableWithPagination
                    headers={tableHeaders}
                    rows={productsPageLevelList}
                    itemsPerPage={20}
                    useModal={true}
                    isDataLoading={isSearched}
                    isRowsExpandable={false}
                    setModalVisible={setModalVisible}
                    setSelected={setSelected}
                    isDetail={true}
                    handleRowClick={() => { }}
                    handleDeleteRow={() => { }}
                    handleSortColumn={onHandleSortColumn}
                    bottomCornerRadius={16}
                  />
                )}
              </View>
              {isSmallDevice && (
                <View style={cardStyles.cardList}>
                  {productsPageLevelList.length === 0 ? (
                    <View style={cardStyles.emptyState}>
                      <Ionicons name="cube-outline" size={48} color="#CBD5E1" />
                      <Text style={cardStyles.emptyStateText}>{t("noDataAvailable")}</Text>
                    </View>
                  ) : (
                    <>
                      {productsPageLevelList
                        .slice(mobilePage * itemsPerPage, (mobilePage + 1) * itemsPerPage)
                        .map((product, index) => (
                          <MobileProductCard key={product.ItemCode || index} product={product} />
                        ))}
                      <DataTable.Pagination
                        page={mobilePage}
                        numberOfPages={Math.ceil(productsPageLevelList.length / itemsPerPage)}
                        onPageChange={(page) => setMobilePage(page)}
                        label={`${mobilePage * itemsPerPage + 1}-${Math.min(
                          (mobilePage + 1) * itemsPerPage,
                          productsPageLevelList.length
                        )} of ${productsPageLevelList.length}`}
                        showFastPaginationControls
                      />
                    </>
                  )}
                </View>
              )}
            </>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </ScrollView>
      {isSmallDevice && contentOffSet > 200 && (
        <TouchableOpacity
          style={{
            position: "absolute",
            right: 16,
            bottom: isAtBottom ? 110 : 16,
            backgroundColor: isDark ? colors.primary : "#2F95DC",
            width: 48,
            height: 48,
            borderRadius: 24,
            justifyContent: "center",
            alignItems: "center",
            opacity: 0.85,
            elevation: 4,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
          }}
          onPress={() => {
            listRef.current?.scrollTo({ y: 0, animated: true });
          }}
        >
          <Ionicons name="arrow-up" size={24} color="white" />
        </TouchableOpacity>
      )}
      <Modal.ModalContainer
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        childern={ModalContent}
      />
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean) => StyleSheet.create({
  tableCard: {
    marginHorizontal: 16,
    marginBottom: 24,
    marginTop: 12,
    backgroundColor: isDark ? colors.surface : "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: isDark ? colors.border : "#E2E8F0",
    shadowColor: isDark ? colors.text : "#0F172A",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 4,
  },
  pageWrapper: {
    flex: 1,
    minHeight: "100%" as any,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
});

const createProductFilterStyles = (colors: any) => StyleSheet.create({
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 0,
    marginTop: 4,
    paddingBottom: 8,
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 8,
    gap: 8,
    alignItems: "center",
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
    fontWeight: "600",
    fontSize: 13,
    color: colors.text,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: colors.surfaceSubtle,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 999,
    marginRight: 8,
  },
  activeFilterButton: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  filterText: {
    fontSize: 13,
    fontWeight: "500",
    color: colors.textSecondary,
  },
  activeFilterText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
  },
  mobileFilterWrapper: {
    width: "100%",
    marginBottom: 4,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: colors.surfaceSubtle,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 999,
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryBg,
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.textSecondary,
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: colors.primary,
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: colors.textSecondary,
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
});

function createCardStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    cardList: {
      paddingHorizontal: 16,
      paddingBottom: 24,
      paddingTop: 8,
      gap: 12,
      backgroundColor: "transparent",
    },
    emptyState: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingTop: 60,
    },
    emptyStateText: {
      marginTop: 12,
      fontSize: 16,
      color: "#9CA3AF",
      fontWeight: "500",
    },
    tableCard: {
      marginHorizontal: 16,
      marginBottom: 24,
      marginTop: 12,
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      overflow: "hidden" as any,
      shadowColor: isDark ? colors.text : "#0F172A",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.07,
      shadowRadius: 12,
      elevation: 4,
    },
    card: {
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E8EDF5",
      shadowColor: isDark ? colors.text : "#1E293B",
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.07,
      shadowRadius: 10,
      elevation: 3,
      overflow: "hidden" as any,
      marginBottom: 4,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
    },
    cardHeaderLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: "transparent",
      flex: 1,
      paddingRight: 10,
    },
    containerIconWrap: {
      width: 34,
      height: 34,
      borderRadius: 10,
      backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
      alignItems: "center",
      justifyContent: "center",
    },
    containerNumber: {
      fontSize: 14,
      fontWeight: "700" as const,
      color: isDark ? colors.text : "#0F172A",
      letterSpacing: 0.2,
      flexWrap: "wrap",
      backgroundColor: "transparent",
    },
    divider: {
      height: 1,
      backgroundColor: isDark ? colors.background : "#eef2f6",
      marginHorizontal: 16,
    },
    dateRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      gap: 4,
    },
    dateBlock: {
      flex: 1,
      alignItems: "center",
      gap: 3,
    },
    dateLabel: {
      fontSize: 10,
      color: isDark ? colors.textSecondary : "#94A3B8",
      fontWeight: "600" as const,
      textTransform: "uppercase" as any,
      letterSpacing: 0.5,
    },
    dateValue: {
      fontSize: 13,
      color: isDark ? colors.text : "#1E293B",
      fontWeight: "700" as const,
    },
    cardFooter: {
      flexDirection: "row",
      flexWrap: "wrap",
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 1,
      borderTopColor: isDark ? colors.background : "#eef2f6",
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
      gap: 8,
    },
    metaChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      paddingVertical: 4,
      paddingHorizontal: 8,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      gap: 4,
    },
    metaChipText: {
      fontSize: 11,
      color: isDark ? colors.textSecondary : "#64748B",
      fontWeight: "500" as const,
    },
  });
}
